/**
 * v1.3.19 — HACCP Schede Tecniche Prodotto — REDESIGN COMPLETO
 *
 * 3 sezioni a Tab:
 *  - "Archivio": tutte le schede (importate + create), filtro, clona, scarica, modifica, elimina.
 *    Include la card "Importa scheda esistente (DOCX)" v1.3.18.
 *  - "Crea nuova scheda": workflow guidato con AI (etichetta → ingredienti+allergeni+nutrizionali),
 *    PDF analisi (nutrizionali+microbiologici), AI organolettiche, foto prodotto, form completo.
 *  - "Template & Placeholder": upload/scarica template DOCX aziendale + template base + lista placeholder
 *    Jinja con copia rapida, per personalizzare il template a piacimento.
 */
import { useEffect, useState, useRef } from 'react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  FileText, Upload, Download, Loader2, FlaskConical, ChefHat, Info, Trash2, AlertTriangle,
  CheckCircle2, FileUp, Copy, Sparkles, Image as ImageIcon, ScanLine, Search, Files, Pencil, Plus, ArrowRightLeft,
} from 'lucide-react';
import { useCurrentPdv } from '../contexts/CurrentPdvContext';

const emptyForm = {
  denominazione: '', ingredienti: '', allergeni: '', peso_netto: '',
  categoria_merceologica: '', shelf_life: '', tmc: '', norme_conservazione: '', norme_consumo: '',
  packaging: '', info_confezionamento: '', caratteristiche_chimico_fisiche: '',
  info_logistiche: '', info_varie: '',
  peso_pezzo: '', peso_cartone: '', cartoni_bancale: '', tara_cartone: '',
  ean_13: '', ean_128: '',
};

// v1.3.39 — parole chiave allergeni UE (per evidenziazione MAIUSCOLO+GRASSETTO a video)
const ALLERGEN_KEYWORDS = [
  'glutine','grano','frumento','farina di grano','farina di frumento','segale','orzo','avena','farro','kamut','spelta','semola','semolino','malto','couscous','seitan','bulgur',
  'crostacei','gambero','gamberi','gamberetti','gamberetto','scampi','aragosta','astice','granchio','mazzancolle','krill',
  'uova','uovo','albume','albumi','tuorlo','ovoalbumina','lisozima',
  'pesce','tonno','salmone','merluzzo','alici','acciughe','sardine','sgombro','branzino','spigola','orata','trota','nasello','platessa','sogliola','colatura di alici','garum',
  'arachidi','arachide','burro di arachidi',
  'soia','tofu','tempeh','edamame','miso','lecitina di soia','salsa di soia',
  'latte','lattosio','siero di latte','panna','burro','formaggio','formaggi','ricotta','mascarpone','mozzarella','parmigiano','grana','pecorino','yogurt','caseina','caseinato','proteine del latte','latte in polvere',
  'mandorla','mandorle','nocciola','nocciole','noce','noci','pistacchio','pistacchi','anacardi','anacardo','pecan','macadamia','noce del brasile',
  'sedano','sedano rapa',
  'senape','semi di senape','mostarda',
  'sesamo','semi di sesamo','tahina','tahini','gomasio',
  'anidride solforosa','solfiti','solfito','metabisolfito','bisolfito',
  'lupino','lupini','farina di lupini',
  'molluschi','cozze','vongole','ostriche','calamaro','calamari','polpo','polpi','seppia','seppie','totano','lumache','capesante',
];
const _allergenRegex = new RegExp('\\b(' + ALLERGEN_KEYWORDS.sort((a,b)=>b.length-a.length).map(s => s.replace(/[.*+?^${}()|[\]\\]/g,'\\$&')).join('|') + ')\\b', 'gi');

function renderHighlightedIngredienti(text) {
  if (!text) return null;
  const parts = text.split(_allergenRegex);
  return parts.map((part, idx) => {
    if (!part) return null;
    // i segmenti dispari sono i match (per via del gruppo di cattura)
    if (idx % 2 === 1) {
      return <strong key={idx} className="uppercase font-bold text-rose-700">{part.toUpperCase()}</strong>;
    }
    return <span key={idx}>{part}</span>;
  });
}

// v1.3.40 — valori microbiologici manuali (righe editabili) + copia valori da altra scheda
const DEFAULT_MICROB_PARAMS = [
  'Carica batterica totale (UFC/g)',
  'Enterobacteriaceae (UFC/g)',
  'Escherichia coli (UFC/g)',
  'Stafilococchi coagulasi positivi (UFC/g)',
  'Salmonella spp. (assente in 25g)',
  'Listeria monocytogenes (assente in 25g)',
  'Lieviti e muffe (UFC/g)',
];
const microbToObj = (rows) => Object.fromEntries((rows || []).filter(r => (r.param || '').trim()).map(r => [r.param.trim(), r.valore]));
const objToMicrobRows = (obj) => Object.entries(obj || {}).map(([param, valore]) => ({ param, valore: String(valore ?? '') }));

export default function SchedeProdottoPage() {
  const { currentAziendaId, currentPdvId } = useCurrentPdv();
  const selectedAz = currentAziendaId || '';
  const selectedPdv = currentPdvId || '';

  const [aziende, setAziende] = useState([]);
  const [pdvList, setPdvList] = useState([]);
  const [schede, setSchede] = useState([]);
  const [templateInfo, setTemplateInfo] = useState(null);
  const [placeholders, setPlaceholders] = useState(null);

  const [filter, setFilter] = useState('');
  const [creating, setCreating] = useState(false);
  const [generating, setGenerating] = useState({});
  const [cloning, setCloning] = useState({});
  const [uploadingTemplate, setUploadingTemplate] = useState(false);
  const [downloadingTplBase, setDownloadingTplBase] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importForm, setImportForm] = useState({ denominazione: '', note: '', file: null });

  // workflow "Crea nuova"
  const [form, setForm] = useState(emptyForm);
  const [nutr, setNutr] = useState({});
  const [microbRows, setMicrobRows] = useState([]);
  const [qual, setQual] = useState({});
  const [aiLabelLoading, setAiLabelLoading] = useState(false);
  const [aiOrgLoading, setAiOrgLoading] = useState(false);
  const [aiPdfLoading, setAiPdfLoading] = useState(false);
  const [fotoProdotto, setFotoProdotto] = useState(null);

  // clone dialog
  const [cloneDialog, setCloneDialog] = useState({ open: false, source: null, name: '' });

  // v1.3.41 — selezione multipla + riassegnazione società/UL
  const [selectedIds, setSelectedIds] = useState([]);
  const [reassignDialog, setReassignDialog] = useState({ open: false, azienda_id: '', pdv_id: '' });
  const [reassignPdvList, setReassignPdvList] = useState([]);
  const [reassigning, setReassigning] = useState(false);

  // v1.3.34 — modifica scheda esistente + tab controllato
  const [editingId, setEditingId] = useState(null);
  const [activeTab, setActiveTab] = useState('archivio');
  // v1.3.35 — stato foto prodotto in modifica (rimozione sfondo)
  const [editingFoto, setEditingFoto] = useState(null); // {hasFoto, bg_removed}
  const [bgToggling, setBgToggling] = useState(false);
  const [fotoPreviewUrl, setFotoPreviewUrl] = useState(null);

  useEffect(() => {
    api.get('/aziende').then(r => setAziende(r.data || [])).catch(() => {});
    api.get('/haccp/schede-prodotto/placeholders').then(r => setPlaceholders(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedAz) { setPdvList([]); setTemplateInfo(null); return; }
    api.get(`/pdv?azienda_id=${selectedAz}`).then(r => setPdvList(r.data || [])).catch(() => setPdvList([]));
    refreshTemplate();
  }, [selectedAz]);

  useEffect(() => {
    if (selectedAz) refresh();
    else { setSchede([]); }
  }, [selectedAz, selectedPdv]);

  const refresh = async () => {
    if (!selectedAz) return;
    const ulQs = selectedPdv ? `&pdv_id=${selectedPdv}` : '';
    const s = await api.get(`/haccp/schede-prodotto/schede?azienda_id=${selectedAz}${ulQs}`);
    setSchede(s.data || []);
  };

  const refreshTemplate = async () => {
    if (!selectedAz) { setTemplateInfo(null); return; }
    try {
      const r = await api.get(`/haccp/schede-prodotto/templates/${selectedAz}/info`);
      setTemplateInfo(r.data);
    } catch { setTemplateInfo(null); }
  };

  // ====== TEMPLATE ======
  const handleUploadTemplate = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingTemplate(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post(`/haccp/schede-prodotto/templates/${selectedAz}/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Template caricato');
      await refreshTemplate();
      e.target.value = '';
    } catch (er) {
      toast.error(er.response?.data?.detail || 'Upload template fallito');
    } finally { setUploadingTemplate(false); }
  };

  const handleDownloadTemplate = async () => {
    try {
      const r = await api.get(`/haccp/schede-prodotto/templates/${selectedAz}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      const az = aziende.find(x => x.id === selectedAz);
      a.href = url; a.download = `template_scheda_${az?.denominazione || selectedAz}.docx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error('Download template fallito'); }
  };

  const handleDeleteTemplate = async () => {
    if (!confirm('Eliminare il template aziendale?')) return;
    try {
      await api.delete(`/haccp/schede-prodotto/templates/${selectedAz}`);
      toast.success('Template eliminato');
      await refreshTemplate();
    } catch { toast.error('Eliminazione fallita'); }
  };

  const handleDownloadTemplateBase = async () => {
    setDownloadingTplBase(true);
    try {
      const r = await api.get(`/haccp/schede-prodotto/template-base/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `template_scheda_prodotto_DA_PERSONALIZZARE.docx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success('Template base scaricato. Personalizzalo con il tuo logo/dati e ricaricalo.');
    } catch { toast.error('Download template base fallito'); }
    finally { setDownloadingTplBase(false); }
  };

  // ====== IMPORT DOCX ======
  const handleImportDocx = async () => {
    if (!importForm.denominazione.trim()) { toast.error('Inserisci la denominazione'); return; }
    if (!importForm.file) { toast.error('Seleziona il file DOCX'); return; }
    if (!selectedAz) { toast.error('Seleziona prima una Società'); return; }
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', importForm.file);
      fd.append('azienda_id', selectedAz);
      fd.append('denominazione', importForm.denominazione);
      if (selectedPdv) fd.append('pdv_id', selectedPdv);
      if (importForm.note) fd.append('note', importForm.note);
      await api.post('/haccp/schede-prodotto/schede/import-docx', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      });
      toast.success(`Scheda "${importForm.denominazione}" importata`);
      setImportForm({ denominazione: '', note: '', file: null });
      const inp = document.getElementById('import-docx-file-input');
      if (inp) inp.value = '';
      await refresh();
    } catch (er) {
      toast.error(er.response?.data?.detail || 'Import fallito');
    } finally { setImporting(false); }
  };

  // ====== WORKFLOW CREA NUOVA ======
  const handleAiLabel = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAiLabelLoading(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const r = await api.post('/haccp/schede-prodotto/extract-from-label', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 90000,
      });
      if (!r.data?.ok) { toast.error('Estrazione AI non riuscita'); return; }
      const d = r.data.data || {};
      // popola form
      setForm(prev => ({
        ...prev,
        denominazione: d.denominazione || prev.denominazione,
        ingredienti: d.ingredienti || prev.ingredienti,
        allergeni: Array.isArray(d.allergeni) ? d.allergeni.join(', ').toUpperCase() : (d.allergeni || prev.allergeni),
        peso_netto: d.peso_netto || prev.peso_netto,
        ean_13: d.ean_13 || prev.ean_13,
        ean_128: d.ean_128 || prev.ean_128,
      }));
      const n = d.valori_nutrizionali_per_100g || {};
      setNutr(prev => ({ ...prev, ...Object.fromEntries(Object.entries(n).filter(([, v]) => v)) }));
      const eanMsg = (d.ean_13 || d.ean_128) ? ` (EAN rilevato)` : '';
      toast.success(`Dati estratti dall'etichetta${eanMsg}`);
      e.target.value = '';
      // v1.3.34 — avvia automaticamente l'AI delle qualità organolettiche coi dati appena estratti
      if ((d.denominazione || '').trim()) {
        await handleAiOrganoleptic({
          denominazione: d.denominazione || '',
          ingredienti: d.ingredienti || '',
          categoria_merceologica: form.categoria_merceologica || '',
        });
      }
    } catch (er) {
      toast.error(er.response?.data?.detail || 'AI Vision fallita');
    } finally { setAiLabelLoading(false); }
  };

  const handleAiPdf = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAiPdfLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/haccp/schede-prodotto/extract-microbiologici-from-pdf', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 90000,
      });
      const d = r.data?.data || {};
      setMicrobRows(prev => objToMicrobRows({ ...microbToObj(prev), ...d }));
      toast.success(`Estratti ${Object.keys(d).length} valori microbiologici`);
      e.target.value = '';
    } catch (er) {
      toast.error(er.response?.data?.detail || 'Estrazione PDF fallita');
    } finally { setAiPdfLoading(false); }
  };

  // v1.3.40 — gestione righe microbiologici manuali
  const updateMicrobRow = (idx, key, val) => setMicrobRows(rows => rows.map((r, i) => i === idx ? { ...r, [key]: val } : r));
  const addMicrobRow = () => setMicrobRows(rows => [...rows, { param: '', valore: '' }]);
  const removeMicrobRow = (idx) => setMicrobRows(rows => rows.filter((_, i) => i !== idx));
  const loadDefaultMicrob = () => setMicrobRows(rows => {
    const existing = new Set(rows.map(r => (r.param || '').trim()));
    const add = DEFAULT_MICROB_PARAMS.filter(p => !existing.has(p)).map(p => ({ param: p, valore: '' }));
    return [...rows, ...add];
  });

  // v1.3.40 — copia i valori (microbiologici + nutrizionali + organolettiche) da un'altra scheda
  const [copyFromId, setCopyFromId] = useState('');
  const copyValuesFrom = (id) => {
    const s = schede.find(x => x.id === id);
    if (!s) return;
    setMicrobRows(objToMicrobRows(s.valori_microbiologici));
    setNutr(s.valori_nutrizionali || {});
    setQual(s.qualita_organolettiche || {});
    toast.success(`Valori copiati da "${s.denominazione}" — ora puoi modificarli`);
  };

  const handleAiOrganoleptic = async (override) => {
    const ov = (override && typeof override === 'object' && 'denominazione' in override) ? override : null;
    const denom = (ov?.denominazione ?? form.denominazione) || '';
    const ingr = (ov?.ingredienti ?? form.ingredienti) || '';
    const cat = (ov?.categoria_merceologica ?? form.categoria_merceologica) || '';
    if (!denom.trim()) { toast.error('Inserisci prima la denominazione'); return; }
    setAiOrgLoading(true);
    try {
      const r = await api.post('/haccp/schede-prodotto/generate-organoleptic', {
        denominazione: denom,
        ingredienti: ingr,
        categoria_merceologica: cat,
      });
      if (r.data?.ok) { setQual(r.data.data || {}); toast.success('Qualità organolettiche generate'); }
      else { toast.error('Generazione AI fallita'); }
    } catch (er) {
      toast.error(er.response?.data?.detail || 'Errore AI');
    } finally { setAiOrgLoading(false); }
  };

  const handleCreateScheda = async () => {
    if (!form.denominazione.trim()) { toast.error('Inserisci la denominazione'); return; }
    if (!selectedAz) { toast.error('Seleziona una Società'); return; }
    setCreating(true);
    try {
      // v1.3.34 — modalità modifica (PUT) se stiamo editando una scheda esistente
      if (editingId) {
        await api.put(`/haccp/schede-prodotto/schede/${editingId}`, {
          ...form,
          pdv_id: selectedPdv || null,
          valori_nutrizionali: nutr,
          valori_microbiologici: microbToObj(microbRows),
          qualita_organolettiche: qual,
        });
        if (fotoProdotto) {
          try {
            const fd = new FormData();
            fd.append('tipo', 'prodotto');
            fd.append('foto', fotoProdotto);
            const fr = await api.post(`/haccp/schede-prodotto/schede/${editingId}/upload-foto`, fd, {
              headers: { 'Content-Type': 'multipart/form-data' },
            });
            if (fr.data && fr.data.bg_removed === false) {
              toast.warning(`Foto caricata: sfondo NON rimosso${fr.data.bg_error ? ' (' + fr.data.bg_error + ')' : ''}. Puoi riprovare con 'Rimuovi sfondo'.`);
            }
          } catch { toast.warning('Scheda aggiornata ma upload foto fallito'); }
        }
        toast.success('Scheda aggiornata');
        setForm(emptyForm); setNutr({}); setMicrobRows([]); setQual({}); setFotoProdotto(null);
        setEditingId(null);
        setEditingFoto(null);
        setActiveTab('archivio');
        await refresh();
        return;
      }
      const created = await api.post('/haccp/schede-prodotto/schede', {
        azienda_id: selectedAz,
        pdv_id: selectedPdv || null,
        ...form,
        valori_nutrizionali: nutr,
        valori_microbiologici: microbToObj(microbRows),
        qualita_organolettiche: qual,
      });
      // upload foto prodotto se selezionata
      if (fotoProdotto && created.data?.id) {
        try {
          const fd = new FormData();
          fd.append('tipo', 'prodotto');
          fd.append('foto', fotoProdotto);
          const fr = await api.post(`/haccp/schede-prodotto/schede/${created.data.id}/upload-foto`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          if (fr.data && fr.data.bg_removed === false) {
            toast.warning(`Foto caricata: sfondo NON rimosso${fr.data.bg_error ? ' (' + fr.data.bg_error + ')' : ''}. Puoi riprovare dalla modifica con 'Rimuovi sfondo'.`);
          }
        } catch { toast.warning('Scheda creata ma upload foto fallito'); }
      }
      toast.success('Scheda prodotto creata');
      setForm(emptyForm); setNutr({}); setMicrobRows([]); setQual({}); setFotoProdotto(null);
      await refresh();
    } catch (er) {
      toast.error(er.response?.data?.detail || (editingId ? 'Aggiornamento fallito' : 'Creazione fallita'));
    } finally { setCreating(false); }
  };

  // v1.3.34 — carica una scheda esistente nel form per modificarla
  const handleEdit = (s) => {
    const f = { ...emptyForm };
    Object.keys(emptyForm).forEach(k => { if (s[k] !== undefined && s[k] !== null) f[k] = s[k]; });
    setForm(f);
    setNutr(s.valori_nutrizionali || {});
    setMicrobRows(objToMicrobRows(s.valori_microbiologici));
    setQual(s.qualita_organolettiche || {});
    setFotoProdotto(null);
    setEditingId(s.id);
    setEditingFoto({ hasFoto: !!s.foto_prodotto_filename, bg_removed: !!s.foto_prodotto_bg_removed });
    if (s.foto_prodotto_filename) { loadFotoPreview(s.id); } else { setFotoPreviewUrl(null); }
    setActiveTab('crea');
    window.scrollTo({ top: 0, behavior: 'smooth' });
    toast.info(`Stai modificando: ${s.denominazione}`);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingFoto(null);
    setForm(emptyForm); setNutr({}); setMicrobRows([]); setQual({}); setFotoProdotto(null);
  };

  // v1.3.35 — alterna foto prodotto con/senza sfondo (bianco)
  // v1.3.35 — carica anteprima foto prodotto (via axios, con auth) come blob URL
  const loadFotoPreview = async (id) => {
    try {
      const r = await api.get(`/haccp/schede-prodotto/schede/${id}/foto/prodotto`, { responseType: 'blob' });
      setFotoPreviewUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(r.data); });
    } catch {
      setFotoPreviewUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return null; });
    }
  };

  const handleToggleSfondo = async () => {
    if (!editingId) return;
    setBgToggling(true);
    try {
      const r = await api.post(`/haccp/schede-prodotto/schede/${editingId}/foto-prodotto/toggle-sfondo`);
      setEditingFoto((p) => ({ ...(p || {}), hasFoto: true, bg_removed: r.data?.bg_removed }));
      toast.success(r.data?.message || 'Aggiornato');
      await loadFotoPreview(editingId);
      await refresh();
    } catch (er) {
      toast.error(er.response?.data?.detail || 'Operazione non riuscita');
    } finally { setBgToggling(false); }
  };

  // ====== LISTA AZIONI ======
  const handleGenera = async (s) => {
    setGenerating(g => ({ ...g, [s.id]: true }));
    try {
      const r = await api.get(`/haccp/schede-prodotto/schede/${s.id}/genera-docx`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      const fallback = `scheda_${s.denominazione.replace(/[^\w-]+/g, '_')}.docx`;
      a.href = url;
      a.download = s.is_imported && s.imported_original_filename ? s.imported_original_filename : fallback;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (er) {
      toast.error(er.response?.data?.detail || 'Download fallito');
    } finally { setGenerating(g => ({ ...g, [s.id]: false })); }
  };

  const openCloneDialog = (s) => setCloneDialog({ open: true, source: s, name: `${s.denominazione} - variante` });
  const handleClone = async () => {
    const s = cloneDialog.source;
    if (!cloneDialog.name.trim()) { toast.error('Inserisci il nome della nuova scheda'); return; }
    setCloning(c => ({ ...c, [s.id]: true }));
    try {
      await api.post(`/haccp/schede-prodotto/schede/${s.id}/clone`, {
        denominazione: cloneDialog.name,
        pdv_id: selectedPdv || null,
        reset_foto: true,
      });
      toast.success(`Scheda "${cloneDialog.name}" creata come copia`);
      setCloneDialog({ open: false, source: null, name: '' });
      await refresh();
    } catch (er) {
      toast.error(er.response?.data?.detail || 'Clonazione fallita');
    } finally { setCloning(c => ({ ...c, [s.id]: false })); }
  };

  const handleDelete = async (s) => {
    if (!confirm(`Eliminare la scheda "${s.denominazione}"?`)) return;
    try {
      await api.delete(`/haccp/schede-prodotto/schede/${s.id}`);
      toast.success('Scheda eliminata');
      await refresh();
    } catch { toast.error('Eliminazione fallita'); }
  };

  // v1.3.41 — selezione multipla + riassegnazione
  const toggleSelect = (id) => setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const toggleSelectAll = () => setSelectedIds(prev => prev.length === filteredSchede.length ? [] : filteredSchede.map(s => s.id));

  // v1.3.42 — duplica con peso diverso + generazione bulk DOCX/PDF
  const [pesoDialog, setPesoDialog] = useState({ open: false, nuovo_peso: '' });
  const [bulkBusy, setBulkBusy] = useState(false);
  const confirmDuplicaPeso = async () => {
    if (!pesoDialog.nuovo_peso.trim()) { toast.error('Inserisci il nuovo peso (es. 1000 g)'); return; }
    setBulkBusy(true);
    try {
      const r = await api.post('/haccp/schede-prodotto/schede/bulk/duplica-peso', { scheda_ids: selectedIds, nuovo_peso: pesoDialog.nuovo_peso.trim() });
      const skip = r.data.saltate ? ` (${r.data.saltate} importate saltate)` : '';
      toast.success(`${r.data.creati} scheda/e duplicate con peso "${pesoDialog.nuovo_peso.trim()}"${skip}`);
      setPesoDialog({ open: false, nuovo_peso: '' });
      setSelectedIds([]);
      await refresh();
    } catch (er) { toast.error(er.response?.data?.detail || 'Duplicazione fallita'); }
    finally { setBulkBusy(false); }
  };
  const bulkDownload = async (kind) => {
    if (!selectedIds.length) { toast.error('Seleziona almeno una scheda'); return; }
    setBulkBusy(true);
    const url = kind === 'pdf' ? '/haccp/schede-prodotto/schede/bulk/genera-pdf-zip' : '/haccp/schede-prodotto/schede/bulk/genera-docx-zip';
    try {
      const r = await api.post(url, { scheda_ids: selectedIds }, { responseType: 'blob', timeout: 180000 });
      const blob = new Blob([r.data], { type: 'application/zip' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = kind === 'pdf' ? 'Schede_PDF.zip' : 'Schede_DOCX.zip';
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(a.href);
      toast.success(`ZIP ${kind.toUpperCase()} generato (${selectedIds.length} schede)`);
    } catch (er) {
      let msg = 'Generazione fallita';
      try { const t = await er.response.data.text(); msg = JSON.parse(t).detail || msg; } catch { /* noop */ }
      toast.error(msg);
    } finally { setBulkBusy(false); }
  };

  const openReassign = () => { if (!selectedIds.length) { toast.error('Seleziona almeno una scheda'); return; } setReassignDialog({ open: true, azienda_id: '', pdv_id: '' }); setReassignPdvList([]); };
  const onReassignAziendaChange = async (azId) => {
    setReassignDialog(d => ({ ...d, azienda_id: azId, pdv_id: '' }));
    if (!azId) { setReassignPdvList([]); return; }
    try { const r = await api.get(`/pdv?azienda_id=${azId}`); setReassignPdvList(r.data || []); }
    catch { setReassignPdvList([]); }
  };
  const confirmReassign = async () => {
    if (!reassignDialog.azienda_id) { toast.error('Seleziona la Società di destinazione'); return; }
    setReassigning(true);
    try {
      const r = await api.put('/haccp/schede-prodotto/schede/bulk/riassegna', {
        scheda_ids: selectedIds,
        azienda_id: reassignDialog.azienda_id,
        pdv_id: reassignDialog.pdv_id || null,
      });
      const az = aziende.find(a => a.id === reassignDialog.azienda_id);
      toast.success(`${r.data.modificate} scheda/e riassegnate a "${az?.denominazione || 'società'}"`);
      setReassignDialog({ open: false, azienda_id: '', pdv_id: '' });
      setSelectedIds([]);
      await refresh();
    } catch (er) { toast.error(er.response?.data?.detail || 'Riassegnazione fallita'); }
    finally { setReassigning(false); }
  };

  // v1.3.38 — Cambia foto prodotto direttamente dall'archivio (senza aprire la modifica)
  const changeFotoInputRef = useRef(null);
  const [fotoTargetId, setFotoTargetId] = useState(null);
  const [fotoUploadingId, setFotoUploadingId] = useState(null);
  const triggerChangeFoto = (s) => { setFotoTargetId(s.id); changeFotoInputRef.current?.click(); };
  const handleChangeFotoFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    const targetId = fotoTargetId;
    if (!file || !targetId) { setFotoTargetId(null); return; }
    setFotoUploadingId(targetId);
    try {
      const fd = new FormData();
      fd.append('tipo', 'prodotto');
      fd.append('foto', file);
      const fr = await api.post(`/haccp/schede-prodotto/schede/${targetId}/upload-foto`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (fr.data?.bg_removed === false) {
        toast.warning(`Foto aggiornata: sfondo NON rimosso${fr.data.bg_error ? ' (' + fr.data.bg_error + ')' : ''}.`);
      } else {
        toast.success('Foto prodotto aggiornata (sfondo rimosso)');
      }
      await refresh();
    } catch (er) {
      toast.error(er.response?.data?.detail || 'Cambio foto fallito');
    } finally { setFotoUploadingId(null); setFotoTargetId(null); }
  };

  const filteredSchede = schede.filter(s =>
    !filter || s.denominazione?.toLowerCase().includes(filter.toLowerCase())
  );

  const copyPh = async (snippet) => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(snippet);
        toast.success(`Copiato: ${snippet}`);
        return;
      }
    } catch { /* fallback */ }
    // Fallback per contesti senza Clipboard API (iframe, preview sandbox)
    try {
      const ta = document.createElement('textarea');
      ta.value = snippet; ta.style.position = 'fixed'; ta.style.opacity = '0';
      document.body.appendChild(ta); ta.select();
      document.execCommand('copy');
      ta.remove();
      toast.success(`Copiato: ${snippet}`);
    } catch {
      toast.info(`Copia manuale: ${snippet}`);
    }
  };

  if (!selectedAz) {
    return (
      <div className="space-y-4">
        <div className="page-header">
          <h1 className="page-title flex items-center gap-2"><ChefHat className="w-7 h-7 text-rose-600" /> Schede Tecniche Prodotto</h1>
          <p className="page-subtitle">Archivio, creazione AI-assisted e gestione template DOCX.</p>
        </div>
        <Card className="border-dashed border-2 border-slate-300">
          <CardContent className="py-10 text-center text-slate-500">
            <AlertTriangle className="w-10 h-10 mx-auto mb-3 text-amber-500" />
            <p className="text-base font-medium">Seleziona una Società dalla sidebar per continuare</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="page-header">
        <h1 className="page-title flex items-center gap-2"><ChefHat className="w-7 h-7 text-rose-600" /> Schede Tecniche Prodotto</h1>
        <p className="page-subtitle">
          Archivio multi-tenant, AI Vision per etichette, generazione organolettiche AI,
          template DOCX personalizzabile con placeholder Jinja.
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3" data-testid="schede-tabs">
          <TabsTrigger value="archivio" data-testid="tab-archivio"><Files className="w-4 h-4 mr-1.5" /> Archivio</TabsTrigger>
          <TabsTrigger value="crea" data-testid="tab-crea"><Sparkles className="w-4 h-4 mr-1.5" /> Crea nuova scheda</TabsTrigger>
          <TabsTrigger value="template" data-testid="tab-template"><FileText className="w-4 h-4 mr-1.5" /> Template & Placeholder</TabsTrigger>
        </TabsList>

        {/* ============================ ARCHIVIO ============================ */}
        <TabsContent value="archivio" className="space-y-4 mt-4">
          {/* Import DOCX esistente */}
          <Card className="border-2 border-sky-200 bg-sky-50/30">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2"><FileUp className="w-4 h-4 text-sky-600" /> Importa scheda esistente (DOCX)</CardTitle>
              <p className="text-xs text-slate-600 mt-1">
                Hai già delle schede in Word? Caricale qui: rimangono nell&apos;archivio dell&apos;azienda e si possono
                riscaricare byte-per-byte oppure usare come BASE per nuove schede (clonazione).
              </p>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Denominazione *</Label>
                  <Input value={importForm.denominazione} onChange={e => setImportForm({ ...importForm, denominazione: e.target.value })}
                    placeholder="Es. Limoncino classico" data-testid="import-denom-input" />
                </div>
                <div>
                  <Label className="text-xs">Note (opzionale)</Label>
                  <Input value={importForm.note} onChange={e => setImportForm({ ...importForm, note: e.target.value })}
                    placeholder="Es. revisione 2024" />
                </div>
                <div>
                  <Label className="text-xs">File DOCX *</Label>
                  <Input id="import-docx-file-input" type="file" accept=".docx"
                    onChange={e => setImportForm({ ...importForm, file: e.target.files?.[0] || null })}
                    data-testid="import-docx-file" />
                </div>
              </div>
              <Button onClick={handleImportDocx} disabled={importing || !importForm.file || !importForm.denominazione.trim()}
                className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="import-docx-btn">
                {importing ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Importazione...</> : <><FileUp className="w-4 h-4 mr-1" /> Importa scheda DOCX</>}
              </Button>
            </CardContent>
          </Card>

          {/* Filtri + lista */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex flex-row items-center justify-between">
                <CardTitle className="text-base">Schede prodotto archiviate ({filteredSchede.length})</CardTitle>
                <div className="relative w-72">
                  <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-slate-400" />
                  <Input value={filter} onChange={e => setFilter(e.target.value)} className="pl-8 h-9"
                    placeholder="Cerca per denominazione..." data-testid="search-schede" />
                </div>
              </div>
              {filteredSchede.length > 0 && (
                <div className="flex flex-wrap items-center gap-2 mt-3 p-2 rounded-md bg-indigo-50/60 border border-indigo-200">
                  <label className="flex items-center gap-1.5 text-xs text-indigo-900 cursor-pointer">
                    <input type="checkbox" checked={selectedIds.length === filteredSchede.length && filteredSchede.length > 0}
                      onChange={toggleSelectAll} data-testid="select-all-schede" />
                    Seleziona tutte
                  </label>
                  <span className="text-xs text-indigo-700">{selectedIds.length} selezionate</span>
                  <div className="flex flex-wrap gap-2 ml-auto">
                    <Button size="sm" variant="outline" disabled={!selectedIds.length || bulkBusy} onClick={() => setPesoDialog({ open: true, nuovo_peso: '' })}
                      className="border-amber-400 text-amber-700" data-testid="duplica-peso-btn">
                      <Copy className="w-3.5 h-3.5 mr-1" /> Duplica con peso diverso
                    </Button>
                    <Button size="sm" variant="outline" disabled={!selectedIds.length || bulkBusy} onClick={() => bulkDownload('docx')}
                      className="border-rose-400 text-rose-700" data-testid="bulk-docx-btn">
                      {bulkBusy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1" />} Genera DOCX (ZIP)
                    </Button>
                    <Button size="sm" variant="outline" disabled={!selectedIds.length || bulkBusy} onClick={() => bulkDownload('pdf')}
                      className="border-emerald-400 text-emerald-700" data-testid="bulk-pdf-btn">
                      {bulkBusy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <FileText className="w-3.5 h-3.5 mr-1" />} Converti in PDF (ZIP)
                    </Button>
                    {aziende.length > 1 && (
                      <Button size="sm" variant="outline" disabled={!selectedIds.length} onClick={openReassign}
                        className="border-indigo-400 text-indigo-700" data-testid="reassign-btn">
                        <ArrowRightLeft className="w-3.5 h-3.5 mr-1" /> Riassegna Società/UL
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </CardHeader>
            <CardContent>
              <input ref={changeFotoInputRef} type="file" accept="image/*" className="hidden"
                onChange={handleChangeFotoFile} data-testid="change-foto-input" />
              {filteredSchede.length === 0 ? (
                <p className="text-sm text-slate-400 italic py-6 text-center">Nessuna scheda trovata.</p>
              ) : (
                <div className="space-y-2">
                  {filteredSchede.map(s => (
                    <div key={s.id} className="flex items-center gap-2 p-3 border border-slate-200 rounded-lg hover:bg-slate-50 transition">
                      <input type="checkbox" checked={selectedIds.includes(s.id)} onChange={() => toggleSelect(s.id)}
                        className="flex-shrink-0" data-testid={`select-${s.id}`} title="Seleziona per azioni multiple" />
                      <ChefHat className="w-5 h-5 text-rose-500 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold truncate flex items-center gap-2 flex-wrap">
                          {s.denominazione}
                          {s.is_imported && (
                            <Badge variant="outline" className="text-[10px] bg-sky-50 text-sky-800 border-sky-300">Importata DOCX</Badge>
                          )}
                          {s.cloned_from && (
                            <Badge variant="outline" className="text-[10px] bg-purple-50 text-purple-800 border-purple-300">Clonata</Badge>
                          )}
                          {s.is_legacy_no_ul && (
                            <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-800 border-amber-300">Legacy (senza UL)</Badge>
                          )}
                        </div>
                        <div className="text-xs text-slate-500 truncate">
                          Creata {s.created_at ? new Date(s.created_at).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—'} da {s.created_by}
                          {s.categoria_merceologica && <> · {s.categoria_merceologica}</>}
                        </div>
                      </div>
                      <Button size="sm" onClick={() => handleGenera(s)} disabled={generating[s.id]}
                        className="bg-rose-600 hover:bg-rose-700 text-white" data-testid={`gen-${s.id}`}>
                        {generating[s.id] ? <Loader2 className="w-3 h-3 animate-spin" />
                          : <><Download className="w-3 h-3 mr-1" /> {s.is_imported ? 'Scarica originale' : 'Genera DOCX'}</>}
                      </Button>
                      {!s.is_imported && (
                        <Button size="sm" variant="outline" onClick={() => triggerChangeFoto(s)}
                          disabled={fotoUploadingId === s.id}
                          title="Cambia foto prodotto (sfondo rimosso automaticamente)" data-testid={`change-foto-${s.id}`}>
                          {fotoUploadingId === s.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <ImageIcon className="w-3 h-3" />}
                        </Button>
                      )}
                      {!s.is_imported && (
                        <Button size="sm" variant="outline" onClick={() => handleEdit(s)}
                          title="Modifica la scheda" data-testid={`edit-${s.id}`}>
                          <Pencil className="w-3 h-3" />
                        </Button>
                      )}
                      {!s.is_imported && (
                        <Button size="sm" variant="outline" onClick={() => openCloneDialog(s)} disabled={cloning[s.id]}
                          title="Clona come base per un nuovo prodotto" data-testid={`clone-${s.id}`}>
                          {cloning[s.id] ? <Loader2 className="w-3 h-3 animate-spin" /> : <Copy className="w-3 h-3" />}
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => handleDelete(s)} className="text-red-600 hover:bg-red-50">
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ============================ CREA NUOVA SCHEDA ============================ */}
        <TabsContent value="crea" className="space-y-4 mt-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-900 flex items-start gap-2">
            <Info className="w-4 h-4 mt-0.5" />
            <div>L&apos;AI compila automaticamente i campi a partire da: <strong>foto etichetta</strong> (ingredienti/allergeni/nutrizionali),
              <strong> PDF rapporto laboratorio</strong> (microbiologici/nutrizionali), <strong>denominazione + ingredienti</strong> (qualità organolettiche).
              Verifica e correggi prima di salvare.</div>
          </div>

          {/* Step A — AI Etichetta */}
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><ScanLine className="w-4 h-4 text-purple-600" /> A. Foto etichetta &rarr; AI estrae ingredienti, allergeni e valori</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              <Input type="file" accept="image/*" onChange={handleAiLabel} disabled={aiLabelLoading} data-testid="ai-label-input" />
              {aiLabelLoading && <p className="text-xs text-purple-700"><Loader2 className="inline w-3 h-3 animate-spin" /> Analisi AI in corso (5-15s)...</p>}
            </CardContent>
          </Card>

          {/* Step B — PDF analisi + inserimento manuale */}
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><FlaskConical className="w-4 h-4 text-emerald-600" /> B. Valori microbiologici (da PDF o manuali)</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label className="text-xs">Rapporto di analisi PDF (opzionale) &rarr; estrazione automatica</Label>
                <Input type="file" accept="application/pdf" onChange={handleAiPdf} disabled={aiPdfLoading} data-testid="ai-pdf-input" className="mt-1" />
                {aiPdfLoading && <p className="text-xs text-emerald-700 mt-1"><Loader2 className="inline w-3 h-3 animate-spin" /> Estrazione PDF in corso...</p>}
              </div>

              {schede.length > 0 && (
                <div className="flex flex-wrap items-end gap-2 p-2 rounded border border-sky-200 bg-sky-50/60">
                  <div className="flex-1 min-w-[220px]">
                    <Label className="text-xs text-sky-800">Copia valori (microbiologici, nutrizionali, organolettiche) da un'altra scheda</Label>
                    <select value={copyFromId} onChange={e => setCopyFromId(e.target.value)}
                      className="mt-1 w-full h-9 rounded-md border border-sky-300 bg-white px-2 text-sm" data-testid="copy-values-select">
                      <option value="">— Seleziona una scheda —</option>
                      {schede.map(s => <option key={s.id} value={s.id}>{s.denominazione}</option>)}
                    </select>
                  </div>
                  <Button size="sm" variant="outline" disabled={!copyFromId} onClick={() => copyValuesFrom(copyFromId)}
                    className="border-sky-400 text-sky-700" data-testid="copy-values-btn">Copia valori</Button>
                </div>
              )}

              <div>
                <Label className="text-xs">Parametri microbiologici (modificabili)</Label>
                <div className="space-y-1.5 mt-1" data-testid="microb-rows">
                  {microbRows.map((row, idx) => (
                    <div key={idx} className="flex gap-2 items-center">
                      <Input value={row.param} onChange={e => updateMicrobRow(idx, 'param', e.target.value)} placeholder="Parametro (es. Escherichia coli UFC/g)" className="flex-1" data-testid={`microb-param-${idx}`} />
                      <Input value={row.valore} onChange={e => updateMicrobRow(idx, 'valore', e.target.value)} placeholder="Valore (es. < 10 / Assente)" className="w-44" data-testid={`microb-val-${idx}`} />
                      <Button size="sm" variant="ghost" onClick={() => removeMicrobRow(idx)} data-testid={`microb-del-${idx}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                    </div>
                  ))}
                  {microbRows.length === 0 && <p className="text-xs text-gray-400">Nessun parametro. Carica un PDF, copia da un'altra scheda o aggiungili a mano.</p>}
                </div>
                <div className="flex gap-2 mt-2">
                  <Button size="sm" variant="outline" onClick={addMicrobRow} data-testid="add-microb-row"><Plus className="w-3 h-3 mr-1" /> Aggiungi parametro</Button>
                  <Button size="sm" variant="ghost" onClick={loadDefaultMicrob} data-testid="load-default-microb">Parametri standard</Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Step C — Form completo */}
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><FileText className="w-4 h-4 text-rose-600" /> C. Dati prodotto</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Denominazione *</Label>
                  <Input value={form.denominazione} onChange={e => setForm({...form, denominazione: e.target.value})} data-testid="denom-input" placeholder="Es. Limoncino di Sicilia" />
                </div>
                <div>
                  <Label className="text-xs">Categoria merceologica</Label>
                  <Input value={form.categoria_merceologica} onChange={e => setForm({...form, categoria_merceologica: e.target.value})} placeholder="Es. Liquore agrumato" />
                </div>
                <div>
                  <Label className="text-xs">Peso netto</Label>
                  <Input value={form.peso_netto} onChange={e => setForm({...form, peso_netto: e.target.value})} placeholder="500 ml" />
                </div>
                <div>
                  <Label className="text-xs">Shelf life</Label>
                  <Input value={form.shelf_life} onChange={e => setForm({...form, shelf_life: e.target.value})} placeholder="24 mesi" />
                </div>
                <div>
                  <Label className="text-xs">Termine Minimo di Conservazione (TMC)</Label>
                  <Input value={form.tmc} onChange={e => setForm({...form, tmc: e.target.value})} placeholder="Es. da consumarsi preferibilmente entro il..." data-testid="tmc-input" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Ingredienti</Label>
                <Textarea value={form.ingredienti} onChange={e => setForm({...form, ingredienti: e.target.value})} rows={3} placeholder="Acqua, alcool, zucchero, scorza di limone..." data-testid="ingredienti-textarea" />
                {form.ingredienti && (
                  <div className="mt-1 p-2 rounded border border-rose-200 bg-rose-50/50 text-xs leading-relaxed" data-testid="ingredienti-preview">
                    <span className="text-[10px] uppercase tracking-wide text-rose-500 font-semibold block mb-0.5">Anteprima (allergeni evidenziati)</span>
                    {renderHighlightedIngredienti(form.ingredienti)}
                  </div>
                )}
              </div>
              <div>
                <Label className="text-xs">Allergeni (UE in MAIUSCOLO)</Label>
                <Input value={form.allergeni} onChange={e => setForm({...form, allergeni: e.target.value})} placeholder="Contiene SOLFITI" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Norme di conservazione</Label>
                  <Textarea value={form.norme_conservazione} onChange={e => setForm({...form, norme_conservazione: e.target.value})} rows={2} placeholder="Conservare in luogo fresco e asciutto..." />
                </div>
                <div>
                  <Label className="text-xs">Norme di consumo</Label>
                  <Textarea value={form.norme_consumo} onChange={e => setForm({...form, norme_consumo: e.target.value})} rows={2} placeholder="Servire freddo..." />
                </div>
                <div>
                  <Label className="text-xs">Packaging</Label>
                  <Input value={form.packaging} onChange={e => setForm({...form, packaging: e.target.value})} placeholder="Bottiglia vetro 500ml" />
                </div>
                <div>
                  <Label className="text-xs">Caratteristiche chimico-fisiche</Label>
                  <Input value={form.caratteristiche_chimico_fisiche} onChange={e => setForm({...form, caratteristiche_chimico_fisiche: e.target.value})} placeholder="32% vol., pH 3.5" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Step D — Organolettiche AI */}
          <Card>
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2"><Sparkles className="w-4 h-4 text-fuchsia-600" /> D. Qualità organolettiche (AI)</CardTitle>
              <Button size="sm" onClick={handleAiOrganoleptic} disabled={aiOrgLoading || !form.denominazione.trim()}
                className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white" data-testid="ai-organolettic-btn">
                {aiOrgLoading ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Generazione...</> : <><Sparkles className="w-3 h-3 mr-1" /> Genera con AI</>}
              </Button>
            </CardHeader>
            <CardContent className="space-y-2">
              {['aspetto', 'odore', 'aroma', 'consistenza', 'sapore', 'succosita'].map(k => (
                <div key={k}>
                  <Label className="text-xs capitalize">{k}</Label>
                  <Textarea value={qual[k] || ''} onChange={e => setQual({...qual, [k]: e.target.value})} rows={2} placeholder={`Descrizione ${k}...`} />
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Step E — Logistica + Foto */}
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><FileText className="w-4 h-4 text-slate-600" /> E. Logistica e foto</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <div><Label className="text-xs">Peso pezzo</Label><Input value={form.peso_pezzo} onChange={e => setForm({...form, peso_pezzo: e.target.value})} /></div>
                <div><Label className="text-xs">Peso cartone</Label><Input value={form.peso_cartone} onChange={e => setForm({...form, peso_cartone: e.target.value})} /></div>
                <div><Label className="text-xs">Cartoni/bancale</Label><Input value={form.cartoni_bancale} onChange={e => setForm({...form, cartoni_bancale: e.target.value})} /></div>
                <div><Label className="text-xs">Tara cartone</Label><Input value={form.tara_cartone} onChange={e => setForm({...form, tara_cartone: e.target.value})} /></div>
                <div><Label className="text-xs">EAN-13</Label><Input value={form.ean_13} onChange={e => setForm({...form, ean_13: e.target.value})} data-testid="ean13-input" placeholder="es. 8001234567890" /></div>
                <div><Label className="text-xs">EAN-128</Label><Input value={form.ean_128} onChange={e => setForm({...form, ean_128: e.target.value})} data-testid="ean128-input" placeholder="es. (01)08001234567890(10)LOTTO" /></div>
              </div>
              <div>
                <Label className="text-xs flex items-center gap-1"><ImageIcon className="w-3 h-3" /> Foto prodotto (verrà inserita nel DOCX)</Label>
                <Input type="file" accept="image/*" onChange={e => setFotoProdotto(e.target.files?.[0] || null)} data-testid="foto-prodotto-input" />
                {fotoProdotto && <p className="text-xs text-emerald-700 mt-1">Foto pronta: {fotoProdotto.name}</p>}
                <p className="text-[11px] text-slate-500 mt-1 flex items-center gap-1">
                  <Sparkles className="w-3 h-3 text-purple-500" />
                  Al caricamento lo sfondo viene rimosso automaticamente (sfondo bianco) per valorizzare il prodotto.
                </p>
                {editingId && editingFoto?.hasFoto && (
                  <div className="mt-2 flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 p-2">
                    {fotoPreviewUrl && (
                      <img src={fotoPreviewUrl} alt="Foto prodotto"
                        className="w-16 h-16 object-contain bg-white rounded border" />
                    )}
                    <div className="flex flex-col gap-1">
                      <span className="text-[11px] text-slate-600">
                        {editingFoto.bg_removed ? 'Sfondo rimosso (bianco)' : 'Foto originale'}
                      </span>
                      <Button type="button" size="sm" variant="outline" onClick={handleToggleSfondo}
                        disabled={bgToggling} data-testid="toggle-sfondo-btn">
                        {bgToggling
                          ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Elaboro...</>
                          : editingFoto.bg_removed
                            ? 'Ripristina originale'
                            : <><Sparkles className="w-3 h-3 mr-1" /> Rimuovi sfondo</>}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
              <div>
                <Label className="text-xs">Note / informazioni varie</Label>
                <Textarea value={form.info_varie} onChange={e => setForm({...form, info_varie: e.target.value})} rows={2} />
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end gap-2 sticky bottom-0 bg-white/95 backdrop-blur p-3 border-t">
            {editingId && (
              <Button variant="outline" onClick={cancelEdit} className="text-amber-700 border-amber-300" data-testid="cancel-edit-btn">
                Annulla modifica
              </Button>
            )}
            <Button variant="outline" onClick={() => { setForm(emptyForm); setNutr({}); setMicrobRows([]); setQual({}); setFotoProdotto(null); }}>
              Reset
            </Button>
            <Button onClick={handleCreateScheda} disabled={creating || !form.denominazione.trim()}
              className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="create-scheda-btn">
              {creating
                ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Salvataggio...</>
                : (editingId
                    ? <><CheckCircle2 className="w-4 h-4 mr-1" /> Salva modifiche</>
                    : <><CheckCircle2 className="w-4 h-4 mr-1" /> Salva scheda prodotto</>)}
            </Button>
          </div>
        </TabsContent>

        {/* ============================ TEMPLATE & PLACEHOLDER ============================ */}
        <TabsContent value="template" className="space-y-4 mt-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2"><FileText className="w-4 h-4 text-indigo-600" /> Template aziendale DOCX</CardTitle>
              <p className="text-xs text-slate-500 mt-1">
                Ogni Società ha il proprio template. Personalizzalo con logo, intestazione e i placeholder qui sotto.
                Quando generi una scheda, il sistema sostituisce automaticamente i placeholder con i dati del prodotto.
              </p>
            </CardHeader>
            <CardContent>
              {templateInfo?.has_template ? (
                <div className="flex flex-col gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded">
                  <div className="flex items-center gap-2 text-sm text-emerald-900">
                    <CheckCircle2 className="w-4 h-4" />
                    <span>Template attivo: <strong>{templateInfo.filename_originale}</strong> ({Math.round((templateInfo.size_bytes || 0)/1024)} KB)</span>
                  </div>
                  <div className="text-xs text-emerald-700">
                    Caricato il {templateInfo.uploaded_at ? new Date(templateInfo.uploaded_at).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                    {templateInfo.uploaded_by_email && <> da <strong>{templateInfo.uploaded_by_email}</strong></>}
                  </div>
                  <div className="flex flex-wrap gap-2 mt-1">
                    <Button size="sm" variant="outline" onClick={handleDownloadTemplate} data-testid="download-template-btn">
                      <Download className="w-3 h-3 mr-1" /> Scarica corrente
                    </Button>
                    <label className="cursor-pointer">
                      <input type="file" accept=".docx" onChange={handleUploadTemplate} disabled={uploadingTemplate} className="hidden" data-testid="replace-template-input" />
                      <span className="inline-flex items-center text-xs px-3 py-1.5 rounded border border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100">
                        {uploadingTemplate ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Caricamento...</> : <><Upload className="w-3 h-3 mr-1" /> Sostituisci</>}
                      </span>
                    </label>
                    <Button size="sm" variant="outline" onClick={handleDeleteTemplate} className="text-red-600 hover:bg-red-50" data-testid="delete-template-btn">
                      <Trash2 className="w-3 h-3 mr-1" /> Elimina
                    </Button>
                    <Button size="sm" variant="outline" onClick={handleDownloadTemplateBase} disabled={downloadingTplBase} className="ml-auto text-blue-700 border-blue-300 hover:bg-blue-50">
                      {downloadingTplBase ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Download className="w-3 h-3 mr-1" />}
                      Template base con placeholder
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-900">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <strong>Nessun template aziendale caricato.</strong>
                    <p className="text-xs mt-1">Le schede generate useranno il template di default con placeholder Jinja. Scarica il template base e personalizzalo.</p>
                    <div className="flex flex-wrap gap-2 mt-2">
                      <label className="cursor-pointer">
                        <input type="file" accept=".docx" onChange={handleUploadTemplate} disabled={uploadingTemplate} className="hidden" />
                        <span className="inline-flex items-center text-xs px-3 py-1.5 rounded bg-amber-600 text-white hover:bg-amber-700">
                          {uploadingTemplate ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Caricamento...</> : <><Upload className="w-3 h-3 mr-1" /> Carica template DOCX</>}
                        </span>
                      </label>
                      <Button size="sm" variant="outline" onClick={handleDownloadTemplateBase} disabled={downloadingTplBase} className="text-blue-700 border-blue-300 hover:bg-blue-50">
                        {downloadingTplBase ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Download className="w-3 h-3 mr-1" />}
                        Scarica template base
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Lista placeholder */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2"><Copy className="w-4 h-4 text-indigo-600" /> Placeholder disponibili nel template</CardTitle>
              <p className="text-xs text-slate-500 mt-1">
                Sintassi: <code className="bg-slate-100 px-1 rounded">{placeholders?.syntax || '{{ NOME }}'}</code> (motore: <strong>{placeholders?.engine || 'docxtpl Jinja2'}</strong>).
                Click su un placeholder per copiarlo, poi incollalo nel tuo DOCX.
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              {placeholders?.note && (
                <div className="text-xs bg-slate-50 border border-slate-200 rounded p-2">{placeholders.note}</div>
              )}
              {(placeholders?.groups || []).map(g => (
                <div key={g.category}>
                  <h4 className="text-sm font-semibold text-slate-700 mb-2">{g.category}</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                    {g.items.map(item => (
                      <button key={item.name} onClick={() => copyPh(item.snippet)}
                        className="text-left p-2 border border-slate-200 rounded hover:border-indigo-300 hover:bg-indigo-50/40 transition group"
                        data-testid={`ph-${item.name}`}>
                        <div className="flex items-center justify-between gap-2">
                          <code className="text-xs font-mono text-indigo-700 truncate">{item.snippet}</code>
                          <Copy className="w-3 h-3 text-slate-400 group-hover:text-indigo-600 flex-shrink-0" />
                        </div>
                        <div className="text-[11px] text-slate-500 mt-0.5 truncate">{item.label}</div>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* DIALOG CLONA */}
      <Dialog open={cloneDialog.open} onOpenChange={(open) => setCloneDialog(s => ({ ...s, open }))}>
        <DialogContent data-testid="clone-dialog">
          <DialogHeader>
            <DialogTitle>Clona scheda &laquo;{cloneDialog.source?.denominazione}&raquo;</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            Tutti i dati (ingredienti, valori, organolettiche, logistica) verranno duplicati nella nuova scheda.
            Le foto NON vengono copiate. Modifica solo ciò che cambia per il nuovo prodotto.
          </p>
          <div>
            <Label className="text-xs">Nome del nuovo prodotto</Label>
            <Input value={cloneDialog.name} onChange={e => setCloneDialog(s => ({ ...s, name: e.target.value }))}
              placeholder="Es. Limoncino al pistacchio" data-testid="clone-name-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCloneDialog({ open: false, source: null, name: '' })}>Annulla</Button>
            <Button onClick={handleClone} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="clone-confirm-btn">
              <Copy className="w-4 h-4 mr-1" /> Crea copia
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DIALOG RIASSEGNA SOCIETÀ/UL */}
      <Dialog open={reassignDialog.open} onOpenChange={(open) => setReassignDialog(s => ({ ...s, open }))}>
        <DialogContent data-testid="reassign-dialog">
          <DialogHeader>
            <DialogTitle>Riassegna {selectedIds.length} scheda/e a un'altra Società</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            Le schede selezionate verranno spostate sulla Società (e Unità Locale) scelta. Il documento DOCX
            generato mostrerà i dati della nuova Società, e gli utenti di quella UL potranno vederle.
          </p>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Società di destinazione *</Label>
              <select value={reassignDialog.azienda_id} onChange={e => onReassignAziendaChange(e.target.value)}
                className="mt-1 w-full h-9 rounded-md border border-slate-300 bg-white px-2 text-sm" data-testid="reassign-azienda-select">
                <option value="">— Seleziona Società —</option>
                {aziende.map(a => <option key={a.id} value={a.id}>{a.denominazione}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Unità Locale (opzionale)</Label>
              <select value={reassignDialog.pdv_id} onChange={e => setReassignDialog(d => ({ ...d, pdv_id: e.target.value }))}
                disabled={!reassignDialog.azienda_id}
                className="mt-1 w-full h-9 rounded-md border border-slate-300 bg-white px-2 text-sm disabled:opacity-50" data-testid="reassign-pdv-select">
                <option value="">— Nessuna UL / tutta la Società —</option>
                {reassignPdvList.map(p => <option key={p.id} value={p.id}>{p.nome || p.denominazione || p.indirizzo || p.id}</option>)}
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReassignDialog({ open: false, azienda_id: '', pdv_id: '' })}>Annulla</Button>
            <Button onClick={confirmReassign} disabled={reassigning || !reassignDialog.azienda_id}
              className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="reassign-confirm-btn">
              {reassigning ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <ArrowRightLeft className="w-4 h-4 mr-1" />} Riassegna
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* DIALOG DUPLICA CON PESO DIVERSO */}
      <Dialog open={pesoDialog.open} onOpenChange={(open) => setPesoDialog(s => ({ ...s, open }))}>
        <DialogContent data-testid="peso-dialog">
          <DialogHeader>
            <DialogTitle>Duplica {selectedIds.length} scheda/e con peso diverso</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            Crea una <strong>copia</strong> di ogni scheda selezionata cambiando <strong>solo il peso netto</strong>.
            Tutto il resto (ingredienti, allergeni, valori, ecc.) resta identico. Gli originali non vengono modificati.
          </p>
          <div>
            <Label className="text-xs">Nuovo peso netto *</Label>
            <Input value={pesoDialog.nuovo_peso} onChange={e => setPesoDialog(s => ({ ...s, nuovo_peso: e.target.value }))}
              placeholder="es. 1000 g" data-testid="nuovo-peso-input" autoFocus />
            <p className="text-[11px] text-slate-500 mt-1">Il peso verrà aggiunto anche al nome (es. "Prodotto (1000 g)") per distinguere la copia.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPesoDialog({ open: false, nuovo_peso: '' })}>Annulla</Button>
            <Button onClick={confirmDuplicaPeso} disabled={bulkBusy || !pesoDialog.nuovo_peso.trim()}
              className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="peso-confirm-btn">
              {bulkBusy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Copy className="w-4 h-4 mr-1" />} Duplica
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
